/* eslint-disable sort-imports */
import { createPortal } from 'react-dom';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { ICriticalOrgUpdateItem } from 'src/services/models/critical-organization-updates.service';
import {
    CriticalUpdateSortColumn,
    CriticalUpdateSortDirection,
    formatNewsDateTime,
} from 'src/utils/critical-organization-updates';

const CHEVRON_COLOR = '#363b49';
const CHEVRON_WIDTH = 10;
const FRENCH_LANGUAGE_CODE = 2;
const MENU_HEIGHT_ESTIMATE = 90;

interface IMenuPosition {
    top?: number;
    bottom?: number;
    right: number;
}

export const CriticalUpdatesActionsMenu: React.FC<{
    item: ICriticalOrgUpdateItem;
}> = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<IMenuPosition>({
        right: 0,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.CriticalOrganizationUpdates.${key}`, { defaultValue });

    useEffect(() => {
        if (!isOpen) return undefined;

        const close = () => setIsOpen(false);
        const closeOnOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;

            if (
                !containerRef.current?.contains(target) &&
                !menuRef.current?.contains(target)
            ) {
                close();
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };

        menuRef.current
            ?.querySelector<HTMLButtonElement>('button:not(:disabled)')
            ?.focus({ preventScroll: true });

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen]);

    const toggleMenu = () => {
        if (!isOpen && toggleRef.current) {
            const rect = toggleRef.current.getBoundingClientRect();
            const { clientHeight, clientWidth } = document.documentElement;
            const opensUp = rect.bottom + MENU_HEIGHT_ESTIMATE > clientHeight;

            setMenuPosition({
                right: clientWidth - rect.right,
                ...(opensUp
                    ? { bottom: clientHeight - rect.top }
                    : { top: rect.bottom }),
            });
        }

        setIsOpen((current) => !current);
    };

    return (
        <div className='critical-updates-actions' ref={containerRef}>
            <button
                ref={toggleRef}
                className='critical-updates-actions__toggle'
                type='button'
                aria-label={text('Actions', 'Actions')}
                aria-haspopup='menu'
                aria-expanded={isOpen}
                onClick={toggleMenu}
            >
                <span aria-hidden='true'>&middot;&middot;&middot;</span>
            </button>
            {isOpen &&
                createPortal(
                    <div
                        className='critical-updates-actions__menu'
                        role='menu'
                        ref={menuRef}
                        style={menuPosition}
                    >
                        <button
                            className='critical-updates-actions__item critical-updates-actions__item--danger'
                            type='button'
                            role='menuitem'
                        >
                            {text('DeleteUpdate', 'Delete Update')}
                        </button>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export interface ICriticalUpdatesTableProps {
    items: ICriticalOrgUpdateItem[];
    sortColumn: CriticalUpdateSortColumn;
    sortDirection: CriticalUpdateSortDirection;
    onSort: (
        column: CriticalUpdateSortColumn,
        direction: CriticalUpdateSortDirection,
    ) => void;
    emptyMessage?: string;
}

export const CriticalUpdatesTable: React.FC<ICriticalUpdatesTableProps> = ({
    items,
    sortColumn,
    sortDirection,
    onSort,
    emptyMessage,
}) => {
    const { t } = useTranslation();
    const text = (key: string, defaultValue: string, column?: string) =>
        t(`SystemAdmin.CriticalOrganizationUpdates.${key}`, {
            defaultValue,
            column,
        });

    const renderSortableHeader = (
        column: CriticalUpdateSortColumn,
        label: string,
    ) => {
        const isActive = sortColumn === column;
        const isAscending = isActive && sortDirection === 'asc';
        const directions: CriticalUpdateSortDirection[] = isActive
            ? [isAscending ? 'desc' : 'asc']
            : ['asc', 'desc'];

        return (
            <div
                role='columnheader'
                aria-sort={
                    isActive
                        ? isAscending
                            ? 'ascending'
                            : 'descending'
                        : 'none'
                }
            >
                <span>{label}</span>
                <span className='critical-updates-table__sort-arrows'>
                    {directions.map((direction) => (
                        <button
                            className='critical-updates-table__sort-control'
                            key={direction}
                            type='button'
                            onClick={() => onSort(column, direction)}
                            aria-label={text(
                                direction === 'asc'
                                    ? 'SortAscending'
                                    : 'SortDescending',
                                direction === 'asc'
                                    ? 'Sort {{column}} ascending'
                                    : 'Sort {{column}} descending',
                                label,
                            )}
                        >
                            <Chevron
                                className={`critical-updates-table__chevron is-${
                                    direction === 'asc' ? 'up' : 'down'
                                }`}
                                width={CHEVRON_WIDTH}
                                fill={CHEVRON_COLOR}
                            />
                        </button>
                    ))}
                </span>
            </div>
        );
    };

    return (
        <div className='critical-updates-table' role='table'>
            <div className='critical-updates-table__row is-header' role='row'>
                <div role='columnheader'>
                    {text('ColumnHeadline', 'Headline')}
                </div>
                {renderSortableHeader(
                    'language',
                    text('ColumnLanguage', 'Language'),
                )}
                {renderSortableHeader(
                    'startDate',
                    text('ColumnStartDate', 'Start Date'),
                )}
                {renderSortableHeader(
                    'endDate',
                    text('ColumnEndDate', 'End Date'),
                )}
                <div role='columnheader'>
                    {text('ColumnActions', 'Actions')}
                </div>
            </div>

            {!items.length && emptyMessage && (
                <div className='critical-updates-table__empty' role='row'>
                    <div role='cell'>{emptyMessage}</div>
                </div>
            )}

            {items.map((item) => (
                <div
                    className='critical-updates-table__row'
                    role='row'
                    key={item.ID}
                >
                    <div
                        className='critical-updates-table__headline'
                        role='cell'
                    >
                        {item.HEADLINE}
                    </div>
                    <div role='cell'>
                        {Number(item.NEWSLANGUAGE) === FRENCH_LANGUAGE_CODE
                            ? text('French', 'French')
                            : text('English', 'English')}
                    </div>
                    <div role='cell'>
                        {formatNewsDateTime(item.VALIDFROMDATE)}
                    </div>
                    <div role='cell'>
                        {formatNewsDateTime(item.VALIDTHROUGHDATE)}
                    </div>
                    <div role='cell'>
                        <CriticalUpdatesActionsMenu item={item} />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default CriticalUpdatesTable;
